package br.com.htms.inspeccaocampo.data

import br.com.htms.inspeccaocampo.platform.PlatformFiles
import br.com.htms.inspeccaocampo.platform.currentTimeMillis

/** Resultado de gravar inspeção no armazenamento durável. */
data class SalvarInspecaoResult(
    val inspecao: InspecaoCompleta,
    /** true se IndexedDB/disco confirmou a gravação. */
    val gravouCache: Boolean,
    /** true se removeu conteudoBase64 para caber na quota (paths das fotos mantidos). */
    val usouJsonEnxuto: Boolean = false,
    val erro: String? = null
)

class InspecaoStore {
    private val dir: String
        get() = PlatformFiles.join(PlatformFiles.documentsDir(), "inspecoes").also {
            PlatformFiles.ensureDir(it)
        }

    fun listar(): List<InspecaoCompleta> =
        PlatformFiles.listFiles(dir)
            .filter { it.endsWith(".json") }
            .mapNotNull { path ->
                runCatching {
                    FormJson.inspecaoFromJson(PlatformFiles.readText(path) ?: return@mapNotNull null)
                }.getOrNull()
            }
            .sortedByDescending { it.criadoEm }

    /**
     * Persiste a inspeção aguardando escrita durável (web: IndexedDB).
     * Se o JSON completo (com base64 das fotos) estourar a quota, grava versão enxuta
     * só com metadados + paths — as fotos já estão em chaves separadas no IDB.
     */
    suspend fun salvar(form: FormInspecao, idEdicao: Long?): SalvarInspecaoResult =
        salvarCompleta(
            InspecaoCompleta(
                id = if (idEdicao != null && idEdicao > 0) idEdicao else currentTimeMillis(),
                criadoEm = currentTimeMillis(),
                formTipo = FormTipo.INSPECAO_CAMPO,
                form = form
            ),
            idEdicao
        )

    suspend fun salvarCompleta(inspecaoBase: InspecaoCompleta, idEdicao: Long?): SalvarInspecaoResult {
        val agora = currentTimeMillis()
        val inspecao = if (idEdicao != null && idEdicao > 0) {
            inspecaoBase.copy(id = idEdicao, criadoEm = agora)
        } else {
            inspecaoBase.copy(
                id = if (inspecaoBase.id > 0) inspecaoBase.id else agora,
                criadoEm = agora
            )
        }
        val path = PlatformFiles.join(dir, "${inspecao.id}.json")
        val fullJson = FormJson.inspecaoToJson(inspecao)
        if (PlatformFiles.writeTextDurable(path, fullJson)) {
            return SalvarInspecaoResult(inspecao = inspecao, gravouCache = true)
        }
        val enxuta = when (inspecao.formTipo) {
            FormTipo.INSPECAO_CAMPO ->
                inspecao.copy(form = inspecao.form.semConteudoBase64())
            FormTipo.RELATORIO_FOTO_ALOJAMENTO ->
                inspecao.copy(relatorioFoto = inspecao.relatorioFoto?.semConteudoBase64())
            FormTipo.CHECKLIST_ALOJAMENTO -> inspecao
            FormTipo.RELATORIO_ALOJAMENTO ->
                inspecao.copy(relatorioFoto = inspecao.relatorioFoto?.semConteudoBase64())
            FormTipo.COMUNICACAO_OCORRENCIAS ->
                inspecao.copy(comunicacaoOcorrencias = inspecao.comunicacaoOcorrencias?.semConteudoBase64())
        }
        val slimJson = FormJson.inspecaoToJson(enxuta)
        if (PlatformFiles.writeTextDurable(path, slimJson)) {
            return SalvarInspecaoResult(
                inspecao = enxuta,
                gravouCache = true,
                usouJsonEnxuto = true
            )
        }
        return SalvarInspecaoResult(
            inspecao = inspecao,
            gravouCache = false,
            erro = "Falha ao gravar no cache do navegador (IndexedDB)."
        )
    }

    fun excluir(id: Long) {
        PlatformFiles.delete(PlatformFiles.join(dir, "$id.json"))
    }
}

class DraftStore {
    private val path: String
        get() = PlatformFiles.join(PlatformFiles.documentsDir(), "rascunho_inspecao.json")

    suspend fun salvar(form: FormInspecao, idEdicao: Long?): Boolean {
        val json = buildString {
            append('{')
            if (idEdicao != null) append("\"idEdicao\":$idEdicao,") else append("\"idEdicao\":null,")
            append("\"form\":")
            append(FormJson.toJson(form))
            append('}')
        }
        if (PlatformFiles.writeTextDurable(path, json)) return true
        // Rascunho enorme: tenta sem base64 embutido
        val slim = buildString {
            append('{')
            if (idEdicao != null) append("\"idEdicao\":$idEdicao,") else append("\"idEdicao\":null,")
            append("\"form\":")
            append(FormJson.toJson(form.semConteudoBase64()))
            append('}')
        }
        return PlatformFiles.writeTextDurable(path, slim)
    }

    fun carregar(): Pair<FormInspecao, Long?>? {
        val text = PlatformFiles.readText(path) ?: return null
        return runCatching {
            val root = JsonMap.parse(text)
            val formNode = root.obj("form") ?: return null
            FormJson.fromJson(formNode.raw) to root.long("idEdicao")
        }.getOrNull()
    }

    fun limpar() {
        PlatformFiles.delete(path)
    }
}

class ConfigStore {
    private val dir: String
        get() = PlatformFiles.join(PlatformFiles.documentsDir(), "app_config").also {
            PlatformFiles.ensureDir(it)
        }

    private val configPath: String
        get() = PlatformFiles.join(dir, "config.json")

    fun carregar(): AppConfig {
        val text = PlatformFiles.readText(configPath) ?: return AppConfig.padrao()
        return runCatching { FormJson.configFromJson(text) }.getOrDefault(AppConfig.padrao())
            .also { InspecaoCatalog.recarregar(it) }
    }

    suspend fun salvar(config: AppConfig): AppConfig {
        PlatformFiles.writeTextDurable(configPath, FormJson.configToJson(config))
        InspecaoCatalog.recarregar(config)
        return config
    }

    fun exportarJsonPath(): String {
        val destino = PlatformFiles.join(PlatformFiles.cacheDir(), "Config_InspecaoCampo.json")
        PlatformFiles.writeText(destino, FormJson.configToJson(carregar()))
        return destino
    }

    suspend fun importarJson(text: String): AppConfig {
        val config = FormJson.configFromJson(text)
        return salvar(config)
    }
}
